// Colon-format duration parsing for the Time settings page and test taking.
// HH:MM = total test duration (minutes); MM:SS = per-question limit (seconds).

export const PER_QUESTION_MIN_SECONDS = 5;
export const PER_QUESTION_MAX_SECONDS = 3600;

const HHMM_RE = /^(\d{1,2}):([0-5]\d)$/;
const MMSS_RE = /^(\d{1,2}):([0-5]\d)$/;

/** Parse "H:MM"/"HH:MM" into total minutes. Returns null when malformed. */
export function parseHHMM(value: string): number | null {
  const match = HHMM_RE.exec(value.trim());
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

/** Parse "M:SS"/"MM:SS" into total seconds. Returns null when malformed. */
export function parseMMSS(value: string): number | null {
  const match = MMSS_RE.exec(value.trim());
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
}

export function formatMinutesAsHHMM(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function formatSecondsAsMMSS(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function isValidTotalDuration(value: string): boolean {
  const minutes = parseHHMM(value);
  return minutes !== null && minutes > 0;
}

export function isValidPerQuestionDuration(value: string): boolean {
  const seconds = parseMMSS(value);
  return (
    seconds !== null &&
    seconds >= PER_QUESTION_MIN_SECONDS &&
    seconds <= PER_QUESTION_MAX_SECONDS
  );
}

/** A spent duration read like a clock: "36:44", or "1:06:22" past an hour; "—" when there is none. */
export function formatClockDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds <= 0) return '—';
  const s = Math.round(totalSeconds);
  const hours = Math.floor(s / 3600);
  const minutes = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const seconds = String(s % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
}
